/* One run's breaks: what disagreed, keyed by a masked key, with a distance and never a value.
 *
 * The reference's third screen, and the one where the masking rule stops being a claim and becomes
 * what a reader is actually looking at. Every column here is either a digest, a preview the source
 * rendered, a field *name*, or a numeric distance. There is no operation in this API that returns a
 * field's value from either side, so this screen could not show one if it wanted to.
 *
 * **The truncation band is the most important thing on the page when it appears.** The API bounds the
 * list at 200 while the run's own counts stay exact, so a reader looking at forty rows may be looking
 * at forty of eleven thousand. Absence from this list is therefore not evidence — and a screen that
 * did not say so would invite exactly the conclusion the counts contradict.
 *
 * **The filter counts count what is loaded, not what the run found.** A chip reading "Mismatch (469)"
 * over a truncated list of five would promise 469 rows a click cannot produce. The band above carries
 * the run's real figures; the chips carry the list's.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SectionHead } from '@/shared/ui/SectionHead';
import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useBreaks, useDefinition, useRun } from '../api/reconciliation';
import { definitionPath } from '../model/paths';
import {
  BREAK_FILTERS,
  breakCounts,
  breakKindLabelKey,
  breaksToCsv,
  breakToneOf,
  breakTotalOf,
  filterBreaks,
  formatCount,
  formatDelta,
} from '../model/reconciliation';
import type { BreakFilter } from '../model/reconciliation';

/** How much of a 32-character digest is shown before the hover.
 *
 * Thirteen characters, matching the audit screen's hash treatment. A full MD5 in a scanned cell pushes
 * every other column off the fold, and the whole value is on `title` for whoever is comparing two
 * breaks by hand — which is the only reason to want all 32.
 */
const DIGEST_VISIBLE = 13;

export function RunBreaks(): ReactElement {
  const { t } = useTranslation();
  const { definitionId = '', runId = '' } = useParams<{
    definitionId: string;
    runId: string;
  }>();
  const definition = useDefinition(definitionId);
  const run = useRun(definitionId, runId);
  const breaks = useBreaks(definitionId, runId);
  const [filter, setFilter] = useState<BreakFilter>('all');
  const [exported, setExported] = useState<number | null>(null);

  const loaded = breaks.isSuccess ? breaks.data.items : [];
  const counts = breakCounts(loaded);
  const shown = filterBreaks(loaded, filter);
  const reported = run.isSuccess ? breakTotalOf(run.data) : null;
  const truncated = run.isSuccess && run.data.breaks_truncated;
  const definitionName = definition.isSuccess ? definition.data.name : definitionId;

  return (
    <div className="lq-rc">
      <nav aria-label={t('breadcrumbs.label')} className="lq-breadcrumbs">
        <Link to={SCREEN_PATHS.reconciliation}>{t('reconciliation.title')}</Link>
        <span aria-hidden="true" className="lq-breadcrumbs__sep">
          /
        </span>
        <Link to={definitionPath(definitionId)}>{definitionName}</Link>
        <span aria-hidden="true" className="lq-breadcrumbs__sep">
          /
        </span>
        <span aria-current="page">{t('reconciliation.breaks.crumb')}</span>
      </nav>

      <header className="lq-page-header">
        <div>
          <h1 className="lq-page-header__title lq-rc__detailTitle">
            {t('reconciliation.breaks.title')}
          </h1>
          {run.isSuccess && (
            <p className="lq-page-header__lede">
              {t('reconciliation.breaks.subline', {
                when: new Date(run.data.requested_at).toLocaleString(),
                who: run.data.requested_by,
                mismatched: formatCount(run.data.mismatched ?? 0),
                missing: formatCount(
                  (run.data.left_only ?? 0) + (run.data.right_only ?? 0),
                ),
              })}
            </p>
          )}
        </div>
        <div className="lq-page-header__actions">
          {/* Ghost rather than accent, and the label says what it does. This writes the rows the
              screen holds; it does not queue an export of the full set, because no such capability
              exists — see `breaksToCsv`. */}
          <button
            type="button"
            className="lq-sect__ghost"
            disabled={shown.length === 0}
            onClick={() => {
              downloadCsv(`${definitionName}-${runId}-breaks.csv`, breaksToCsv(shown));
              setExported(shown.length);
            }}
          >
            {t('reconciliation.breaks.export')}
          </button>
        </div>
      </header>
      <div className="lq-pagerule" />

      {truncated && (
        /* Only once the run has been read. "This list is truncated" is a claim about the run, and a
           pending read is not evidence for it. */
        <div className="lq-band lq-band--warn" role="status">
          <div className="lq-band__title">
            {t('reconciliation.breaks.truncated.title')}
          </div>
          <div className="lq-band__body">
            {reported === null
              ? t('reconciliation.breaks.truncated.bodyUnknown', {
                  loaded: loaded.length,
                })
              : t('reconciliation.breaks.truncated.body', {
                  loaded: loaded.length,
                  reported,
                })}
          </div>
        </div>
      )}

      {exported !== null && (
        <div className="lq-flash" role="status">
          <strong className="lq-flash__title">
            {t('reconciliation.breaks.exported.title')}
          </strong>
          <span className="lq-flash__body">
            {t(
              truncated
                ? 'reconciliation.breaks.exported.bodyTruncated'
                : 'reconciliation.breaks.exported.body',
              { count: exported },
            )}
          </span>
        </div>
      )}

      <SectionHead title={t('reconciliation.breaks.section')} />

      {breaks.isPending && <SkeletonRows rows={6} columns={5} />}

      {breaks.isError && (
        <Alert tone="error" title={t('reconciliation.breaks.failed')}>
          {breaks.error.message}
        </Alert>
      )}

      {breaks.isSuccess && loaded.length === 0 ? (
        <EmptyState
          title={t('reconciliation.breaks.empty.title')}
          guidance={t('reconciliation.breaks.empty.body')}
        />
      ) : (
        breaks.isSuccess && (
          <>
            <div className="lq-rc__filters">
              <span className="lq-rc__filterLabel">
                {t('reconciliation.breaks.filterLabel')}
              </span>
              <SegmentedFilter
                variant="ledger"
                label={t('reconciliation.breaks.filterAria')}
                value={filter}
                onChange={setFilter}
                options={BREAK_FILTERS.map((kind) => ({
                  id: kind,
                  label:
                    kind === 'all'
                      ? t('reconciliation.breaks.filter.all')
                      : t(breakKindLabelKey(kind)),
                  /* `all` carries no count: it would restate the row count already under the table,
                     and a chip whose number equals the sum of the other three teaches nothing. The
                     three real kinds carry a zero when they have one, because a zero here is a real
                     answer — "no rows are missing on the left" is what a reader came to find out. */
                  count: kind === 'all' ? undefined : counts[kind],
                }))}
              />
            </div>

            {shown.length === 0 ? (
              <EmptyState
                title={t('reconciliation.breaks.noneOfKind.title')}
                guidance={t('reconciliation.breaks.noneOfKind.body')}
              />
            ) : (
              <DataTable
                variant="ledger"
                caption={t('reconciliation.breaks.caption', { name: definitionName })}
                columns={[
                  { key: 'kind', label: t('reconciliation.column.kind') },
                  { key: 'maskedKey', label: t('reconciliation.column.maskedKey') },
                  { key: 'digest', label: t('reconciliation.column.digest') },
                  { key: 'fields', label: t('reconciliation.column.fields') },
                  {
                    key: 'delta',
                    label: t('reconciliation.column.delta'),
                    numeric: true,
                  },
                ]}
              >
                {shown.map((item) => {
                  const delta = formatDelta(item.numeric_delta);
                  return (
                    <tr key={item.id}>
                      <td className="lq-nowrap">
                        <span
                          className={`lq-tag lq-tag--rcbreak-${breakToneOf(item.kind)}`}
                        >
                          <span className="lq-tag__dot" aria-hidden="true" />
                          {t(breakKindLabelKey(item.kind))}
                        </span>
                      </td>
                      <td className="lq-mono lq-nowrap">{item.masked_key}</td>
                      <td className="lq-rc__digest lq-mono" title={item.key_digest}>
                        {item.key_digest.slice(0, DIGEST_VISIBLE)}…
                      </td>
                      <td className="lq-cell--quiet">
                        {item.fields.length === 0 ? (
                          <>
                            <span className="lq-muted">
                              {t('reconciliation.breaks.noFields')}
                            </span>
                            {/* Why, rather than a bare dash. A missing row has no disagreeing
                                    field because there is no second row to disagree with it — which
                                    is a different fact from "we did not record which field". */}
                            <div className="lq-cell__secondary">
                              {t('reconciliation.breaks.noFieldsWhy')}
                            </div>
                          </>
                        ) : (
                          item.fields.join(', ')
                        )}
                      </td>
                      <td className="lq-table__num">
                        {delta === null ? (
                          <span className="lq-muted">
                            {t('reconciliation.breaks.noDelta')}
                          </span>
                        ) : (
                          <span className="lq-rc__counts">{delta}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </DataTable>
            )}

            <p className="lq-note">{t('reconciliation.breaks.deltaFoot')}</p>
            <p className="lq-note">{t('reconciliation.breaks.maskFoot')}</p>
          </>
        )
      )}
    </div>
  );
}

/* Hands the browser a file. `URL.revokeObjectURL` in the same turn rather than on a timer: the click
 * has already been dispatched synchronously by then, so the blob has served its purpose and leaving it
 * alive would hold the whole list in memory for the life of the tab. */
function downloadCsv(filename: string, body: string): void {
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
